import React, { useState } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import { CheckOutlined, CalendarOutlined, PhoneOutlined, DownOutlined } from '@ant-design/icons';
import { TaskFormModal } from '../TaskForm';
import { ScheduleCallModal, LogCallModal } from '../CallForm';

interface CreateActivityDropdownProps {
    recordId: string;
    modelName: string;
    onCreateActivity?: (recordId: string, modelName: string, type?: string, subType?: string) => void;
    onTaskCreated?: () => void;
    onScheduleCallCreated?: () => void;
    onLogCallCreated?: () => void;
    trigger?: React.ReactNode;
    placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

const CreateActivityDropdown: React.FC<CreateActivityDropdownProps> = ({
    recordId,
    modelName,
    onCreateActivity,
    onTaskCreated,
    onScheduleCallCreated,
    onLogCallCreated,
    trigger,
    placement = 'topLeft'
}) => {
    const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
    const [scheduleCallModalOpen, setScheduleCallModalOpen] = useState(false);
    const [logCallModalOpen, setLogCallModalOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleMenuClick = (info: { key: string }) => {
        const { key } = info;
        // Close dropdown when opening a modal
        setDropdownOpen(false);

        if (key === 'task') {
            setCreateTaskModalOpen(true);
        } else if (key === 'schedule-call') {
            setScheduleCallModalOpen(true);
        } else if (key === 'log-call') {
            setLogCallModalOpen(true);
        } else if (onCreateActivity) {
            onCreateActivity(recordId, modelName, key);
        } else {
            console.log('Create activity:', { recordId, modelName, type: key });
        }
    };

    const callSubMenu: MenuProps['items'] = [
        {
            key: 'schedule-call',
            label: 'Schedule a call',
            onClick: handleMenuClick,
        },
        {
            key: 'log-call',
            label: 'Log a call',
            onClick: handleMenuClick,
        },
    ];

    const menuItems: MenuProps['items'] = [
        {
            key: 'task',
            label: 'Create Task',
            icon: <CheckOutlined />,
            onClick: handleMenuClick,
        },
        {
            key: 'meeting',
            label: 'Create Meeting',
            icon: <CalendarOutlined />,
            onClick: handleMenuClick,
        },
        {
            key: 'call',
            label: 'Create Call',
            icon: <PhoneOutlined />,
            children: callSubMenu,
        },
    ];

    return (
        <>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement={placement}
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
            >
                {trigger || (
                    <Button
                        type="primary"
                        size="small"
                        icon={<DownOutlined />}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                        Create Activity
                    </Button>
                )}
            </Dropdown>
            <TaskFormModal
                open={createTaskModalOpen}
                onCancel={() => setCreateTaskModalOpen(false)}
                onSuccess={() => {
                    if (onTaskCreated) {
                        onTaskCreated();
                    }
                    if (onCreateActivity) {
                        onCreateActivity(recordId, modelName, 'task');
                    }
                }}
                recordId={recordId}
                modelName={modelName}
            />
            <ScheduleCallModal
                open={scheduleCallModalOpen}
                onCancel={() => setScheduleCallModalOpen(false)}
                onSuccess={() => {
                    if (onScheduleCallCreated) {
                        onScheduleCallCreated();
                    }
                    if (onCreateActivity) {
                        onCreateActivity(recordId, modelName, 'call', 'schedule');
                    }
                }}
                recordId={recordId}
                modelName={modelName}
            />
            <LogCallModal
                open={logCallModalOpen}
                onCancel={() => setLogCallModalOpen(false)}
                onSuccess={() => {
                    if (onLogCallCreated) {
                        onLogCallCreated();
                    }
                    if (onCreateActivity) {
                        onCreateActivity(recordId, modelName, 'call', 'log');
                    }
                }}
                recordId={recordId}
                modelName={modelName}
            />
        </>
    );
};

export default CreateActivityDropdown;
